import { Request, Response, NextFunction } from 'express';
import { AuthRequest } from '../../middleware/auth.middleware';
import * as service from './messages.service';
import { listUsers } from '../users/users.service';
import { getOrCreateDM } from '../conversations/conversations.service';
import { getIo } from '../../realtime/socket.bootstrap';
import { SocketEvents } from '@ichat/shared-types';
import { prisma } from '../../shared/prisma';
import { AppError } from '../../shared/errors/AppError';

export async function channelMessages(req: Request, res: Response, next: NextFunction) {
  try {
    const messages = await service.getChannelMessages(req.params.channelId, req.query.cursor as string);
    res.json({ success: true, data: messages });
  } catch (err) { next(err); }
}

export async function conversationMessages(req: Request, res: Response, next: NextFunction) {
  try {
    const messages = await service.getConversationMessages(req.params.conversationId, req.query.cursor as string);
    res.json({ success: true, data: messages });
  } catch (err) { next(err); }
}

export async function create(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    const message = await service.createMessage({ ...req.body, senderId: req.user!.sub });
    res.status(201).json({ success: true, data: message });
  } catch (err) { next(err); }
}

export async function validateBroadcast(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    const { audienceType, audienceId } = req.body;
    const senderId = req.user!.sub;

    const currentUser = await prisma.user.findUnique({
      where: { id: senderId },
      include: { department: true, roles: { include: { role: true } } }
    });
    const isAdmin = currentUser?.roles.some((ur: any) => ur.role.name === 'admin' || ur.role.name === 'super_admin');
    const commonDeptNames = ['HR', 'IT', 'Human Resources', 'Information Technology', 'Admin'];
    const isCommonDeptUser = currentUser?.department?.name ? commonDeptNames.includes(currentUser.department.name) : false;

    if (!isAdmin && !isCommonDeptUser) {
      return next(new AppError(403, 'Only common department heads (HR, IT) can send broadcasts.'));
    }

    // In a real implementation, this would dynamically filter based on audienceType (company, department, team)
    // For now, we reuse listUsers and do a mock filter if needed
    const users = await listUsers(undefined, senderId, 10000);
    let targetUsers = users.filter(u => u.id !== senderId);

    if (audienceType === 'department' && audienceId) {
      targetUsers = targetUsers.filter(u => Array.isArray(audienceId) ? audienceId.includes(u.departmentId) : u.departmentId === audienceId);
    } else if (audienceType === 'company' && audienceId) {
      targetUsers = targetUsers.filter(u => Array.isArray(audienceId) ? audienceId.includes(u.companyId) : u.companyId === audienceId);
    } else if (audienceType === 'users' && Array.isArray(audienceId)) {
      targetUsers = targetUsers.filter(u => audienceId.includes(u.id));
    }

    res.json({ success: true, data: { recipientCount: targetUsers.length } });
  } catch (err) { next(err); }
}

export async function broadcast(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    const { title, content, category, priority, audienceType, audienceId, options } = req.body;
    const senderId = req.user!.sub;

    const currentUser = await prisma.user.findUnique({
      where: { id: senderId },
      include: { department: true, roles: { include: { role: true } } }
    });
    const isAdmin = currentUser?.roles.some((ur: any) => ur.role.name === 'admin' || ur.role.name === 'super_admin');
    const commonDeptNames = ['HR', 'IT', 'Human Resources', 'Information Technology', 'Admin'];
    const isCommonDeptUser = currentUser?.department?.name ? commonDeptNames.includes(currentUser.department.name) : false;

    if (!isAdmin && !isCommonDeptUser) {
      return next(new AppError(403, 'Only common department heads (HR, IT) can send broadcasts.'));
    }

    const users = await listUsers(undefined, senderId, 10000);
    let targetUsers = users.filter(u => u.id !== senderId);

    if (audienceType === 'department' && audienceId) {
      targetUsers = targetUsers.filter(u => Array.isArray(audienceId) ? audienceId.includes(u.departmentId) : u.departmentId === audienceId);
    } else if (audienceType === 'company' && audienceId) {
      targetUsers = targetUsers.filter(u => Array.isArray(audienceId) ? audienceId.includes(u.companyId) : u.companyId === audienceId);
    } else if (audienceType === 'users' && Array.isArray(audienceId)) {
      targetUsers = targetUsers.filter(u => audienceId.includes(u.id));
    }

    console.log(`[Broadcast] Sender ${senderId} broadcasting to ${audienceType} (${targetUsers.length} targets).`);

    const io = getIo();
    let sentCount = 0;

    // Construct rich message content
    let finalContent = content;
    if (title) {
      finalContent = `**${title}**\n\n${content}`;
    }

    const metadata = {
      isBroadcast: true,
      category: category || 'general',
      priority: priority || 'normal',
      options: options || {}
    };

    for (const user of targetUsers) {
      const dmConv = await getOrCreateDM(senderId, user.id);
      const message = await service.createMessage({
        conversationId: dmConv.id,
        senderId,
        content: finalContent,
        contentType: 'markdown',
        metadata
      });

      if (io) {
        io.to(`conversation:${dmConv.id}`).emit(SocketEvents.MESSAGE_NEW, message);
        io.to(`user:${user.id}`).emit(SocketEvents.NOTIFICATION_NEW, { type: 'message', messageId: message.id });
        io.to(`user:${user.id}`).emit(SocketEvents.MESSAGE_NEW, message);
      }
      sentCount++;
    }

    if (io && sentCount > 0) {
      io.to('admin:chat_logs').emit('ADMIN_CHAT_LOG_PING');
    }

    res.json({ success: true, data: { sentCount } });
  } catch (err) { next(err); }
}

export async function listBroadcasts(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    const senderId = req.user!.sub;
    const currentUser = await prisma.user.findUnique({
      where: { id: senderId },
      include: { department: true, roles: { include: { role: true } } }
    });
    const isAdmin = currentUser?.roles.some((ur: any) => ur.role.name === 'admin' || ur.role.name === 'super_admin');
    const commonDeptNames = ['HR', 'IT', 'Human Resources', 'Information Technology', 'Admin'];
    const isCommonDeptUser = currentUser?.department?.name ? commonDeptNames.includes(currentUser.department.name) : false;
    if (!isAdmin && !isCommonDeptUser) return next(new AppError(403, 'Forbidden'));

    // Return unique broadcast messages sent by this user (one per broadcast, identified by metadata.isBroadcast)
    const messages = await prisma.message.findMany({
      where: {
        senderId,
        isDeleted: false,
        metadata: { path: ['isBroadcast'], equals: true }
      },
      orderBy: { createdAt: 'desc' },
      take: 50,
      select: {
        id: true, content: true, metadata: true, createdAt: true, conversationId: true,
        conversation: { select: { members: { select: { userId: true } } } }
      },
    });

    // Deduplicate: group by content+createdAt to represent one broadcast campaign
    const seen = new Set<string>();
    const unique = messages.filter(m => {
      const key = (m.metadata as any)?.broadcastId || m.content;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    res.json({ success: true, data: unique });
  } catch (err) { next(err); }
}

export async function deleteBroadcast(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    const senderId = req.user!.sub;
    const broadcastId = req.params.broadcastId;

    const currentUser = await prisma.user.findUnique({
      where: { id: senderId },
      include: { department: true, roles: { include: { role: true } } }
    });
    const isAdmin = currentUser?.roles.some((ur: any) => ur.role.name === 'admin' || ur.role.name === 'super_admin');
    const commonDeptNames = ['HR', 'IT', 'Human Resources', 'Information Technology', 'Admin'];
    const isCommonDeptUser = currentUser?.department?.name ? commonDeptNames.includes(currentUser.department.name) : false;
    if (!isAdmin && !isCommonDeptUser) return next(new AppError(403, 'Forbidden'));

    // Find the reference message
    const refMsg = await prisma.message.findUnique({ where: { id: broadcastId } });
    if (!refMsg) return next(new AppError(404, 'Broadcast not found'));
    if (refMsg.senderId !== senderId && !isAdmin) return next(new AppError(403, 'Cannot delete another user\'s broadcast'));

    // Delete all messages with same content sent by this sender that are broadcasts
    const deleted = await prisma.message.updateMany({
      where: {
        senderId: refMsg.senderId,
        content: refMsg.content,
        isDeleted: false,
        metadata: { path: ['isBroadcast'], equals: true }
      },
      data: { isDeleted: true, content: 'Broadcast Deleted' }
    });

    // Notify via socket
    const io = getIo();
    if (io) {
      io.emit('BROADCAST_DELETED', { broadcastId, deletedBy: senderId });
    }

    res.json({ success: true, data: { deletedCount: deleted.count } });
  } catch (err) { next(err); }
}

export async function edit(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    const message = await service.editMessage(req.params.id, req.user!.sub, req.body.content);
    res.json({ success: true, data: message });
  } catch (err) { next(err); }
}

export async function remove(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    await service.deleteMessage(req.params.id, req.user!.sub);
    res.json({ success: true });
  } catch (err) { next(err); }
}

export async function react(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    await service.addReaction(req.params.id, req.user!.sub, req.body.emoji);
    res.json({ success: true });
  } catch (err) { next(err); }
}

export async function unreact(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    await service.removeReaction(req.params.id, req.user!.sub, req.body.emoji);
    res.json({ success: true });
  } catch (err) { next(err); }
}

export async function thread(req: Request, res: Response, next: NextFunction) {
  try {
    const replies = await service.getThread(req.params.id);
    res.json({ success: true, data: replies });
  } catch (err) { next(err); }
}

export async function pin(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    const message = await service.pinMessage(req.params.id, req.user!.sub);
    res.json({ success: true, data: message });
  } catch (err) { next(err); }
}

export async function unpin(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    const message = await service.unpinMessage(req.params.id);
    res.json({ success: true, data: message });
  } catch (err) { next(err); }
}

export async function pinnedChannelMessages(req: Request, res: Response, next: NextFunction) {
  try {
    const messages = await service.getPinnedChannelMessages(req.params.channelId);
    res.json({ success: true, data: messages });
  } catch (err) { next(err); }
}

export async function pinnedConversationMessages(req: Request, res: Response, next: NextFunction) {
  try {
    const messages = await service.getPinnedConversationMessages(req.params.conversationId);
    res.json({ success: true, data: messages });
  } catch (err) { next(err); }
}
